import { useEffect, useState } from "react";
import {
  InventoryStockDisplayResponse,
  PaginatedInventoryResponse,
  INVENTORY_UPDATE_REASON_OPTIONS,
} from "../../Interfaces/Warehouse";
import fetchWithAuth from "../../services/fetchWithAuth";
import config from "../../config";
import { formatDateTime } from "../../CommonFunctions";
import "./InventoryChanges.css";
import { useNavigate } from "react-router/dist";

const InventoryChanges = () => {
  const navigate = useNavigate();

  const navigationButtons = [
    {
      text: "View all Pending Orders",
      link: "/warehouse/orders/",
    },
    {
      text: "View all Orders",
      link: "/warehouse/orders/all",
    },
    {
      text: "View Current Inventory",
      link: "/warehouse/inventory-stock",
    },
  ];

  const INVENTORY_UPDATE_REASON_DISPLAY_OPTIONS = [
    { value: "", label: "None" },
    ...INVENTORY_UPDATE_REASON_OPTIONS,
  ];

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchReasoning, setSearchReasoning] = useState<string>("");
  const [filteredUpdates, setFilteredUpdates] =
    useState<PaginatedInventoryResponse | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);

  useEffect(() => {
    document.title = "MamFam Inventory Changes";
  }, []);

  useEffect(() => {
    const fetchFilteredUpdates = async () => {
      try {
        const response = await fetchWithAuth(
          `${config.API_BASE_URL}inventory-stock/paginated?search=${searchQuery}&reason=${searchReasoning}&page=${pageNumber}`,
        );

        if (response.status === 200) {
          const data = await response.json();
          setFilteredUpdates(data);
        } else {
          console.error("Failed to fetch inventory stock:", response.status);
        }
      } catch {
        console.log("Failed to fetch inventory stock");
      }
    };
    fetchFilteredUpdates();
  }, [searchQuery, searchReasoning, pageNumber]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPageNumber(1); // Reset to the first page on search
  };

  return (
    <div>
      <h1>Inventory Changes</h1>
      {navigationButtons.map((button: { text: string; link: string }) => (
        <button onClick={() => navigate(button.link)}>{button.text}</button>
      ))}
      <div className="inventory-changes-search-container">
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search by SKU or by product name"
          className="warehouse-order-search"
        />
        <div className="reason-filters">
          {INVENTORY_UPDATE_REASON_DISPLAY_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`reason-filter-btn ${searchReasoning === option.value ? "active" : ""}`}
              onClick={() =>
                setSearchReasoning(
                  searchReasoning === option.value ? "" : option.value,
                )
              }
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      <div className="table-container">
        <table className="inventory-changes-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Reason</th>
              <th>Change</th>
              <th>Order ID</th>
              <th>Time</th>
              <th>Explanation</th>
            </tr>
          </thead>
          <tbody>
            {filteredUpdates?.results.map(
              (element: InventoryStockDisplayResponse) => (
                <tr key={element.id}>
                  <td>{element.product_name}</td>
                  <td>{element.product_sku}</td>
                  <td>{element.reason}</td>
                  <td>{element.increment}</td>
                  <td>{element.order || "—"}</td>
                  <td>{formatDateTime(element.created_at)}</td>
                  <td>{element.reason_detail}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => setPageNumber(pageNumber - 1)}
          disabled={!filteredUpdates?.previous}
        >
          ← Previous
        </button>
        <span className="pagination-info">Page {pageNumber}</span>
        <button
          className="pagination-btn"
          onClick={() => setPageNumber(pageNumber + 1)}
          disabled={!filteredUpdates?.next}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default InventoryChanges;
